// MED_USERS — github.com/Simple-Life-Medical/med-users
// OpenAPI: github.com/Simple-Life-Medical/med-users/blob/develop/.simple/openapi.yml
//
// One module for the whole med-users surface the funnel talks to. Since DEV-35018 that
// surface is entirely PRE-AUTH: the signup session, v3 account provisioning and the
// session-scoped Telegra intake, all on plain axios with `session_id` as the sole
// capability. The Auth0 DPoP-authenticated endpoints (`POST /users/v1/auth/login`,
// `POST /users/v2/auth/signup`) went away with /signin — they exist on the backend but
// the web no longer has an Auth0 identity to call them with.
import { env } from '@/helpers/environment'
import { createHttpClient, type SentryRequestContext } from './http'

const REPO = 'med-users'
const baseURL = env('MED_USERS_HOST')

const http = createHttpClient({ api: REPO, baseURL, retry: false })

// ── Signup session (unauthenticated, pre-auth) ───────────────────────────────

export async function createSignupSession(data: Record<string, unknown>, sentryContext?: SentryRequestContext) {
	const response = await http.post('/users/v1/signup-session', data, { sentryContext })
	return response.data
}

export async function patchSignupSession(
	sessionId: string,
	data: Record<string, unknown>,
	sentryContext?: SentryRequestContext,
) {
	const response = await http.patch(`/users/v1/signup-session/${sessionId}`, data, { sentryContext })
	return response.data
}

// NB: med-users' POST /signup-session/{id}/telegra/order is RETIRED (endpoint
// deleted). Order creation lives in med-billing now: prescription-review calls
// billing's POST /v1/customers/{session_id}/order (createCustomerOrder in
// src/api/medBilling.ts), billing fills its draft order via med-users
// CreateInitialOrder; the intake's resolveInstances below always returns the
// session's current order through billing (GetCustomerOrder).

// ── KYC precheck (unauthenticated, session-scoped — med-users PR #212) ───────

/**
 * `pass` — личность подтверждена по данным, `job_id` идёт ответом на вопрос загрузки
 * документа. `fallback` — спрашиваем фото ровно как раньше.
 */
type KycPrecheckStatus = 'pass' | 'fallback'

/**
 * Почему решение получилось таким. `passed` идёт с `pass`, любое другое значение — с
 * `fallback`. Словарь бекенда целиком (`KYCPrecheckReason` в openapi.yml); `missing_address`
 * бек добавил сверх тикета — по нему клиент не ветвится, ветвление только по `status`.
 */
type KycPrecheckReason =
	| 'passed'
	| 'low_confidence'
	| 'address_mismatch'
	| 'missing_address'
	| 'missing_contacts'
	| 'age_under_25'
	| 'vouched_error'
	| 'vouched_timeout'
	| 'kill_switch'

export interface KycPrecheckResponse {
	status: KycPrecheckStatus
	reason: KycPrecheckReason
	/** Job id провайдера для ответа на вопрос загрузки документа. Только при `pass`. */
	job_id?: string
	/**
	 * Что сказал провайдер — только при `fallback` и только если он вообще ответил. Скоров и
	 * порогов здесь нет, они остаются на сервере. Мы это не показываем и не ветвимся по нему,
	 * держим для диагностики.
	 */
	details?: {
		address_is_match: boolean
		address_is_valid: boolean
		errors?: Array<{ type: string }>
	}
}

/**
 * Data-only KYC-прекчек (Vouched CrossCheck) для pre-auth интейка: вызывается там, где иначе
 * потребовали бы фото документа. Тела у запроса НЕТ — идентификационные данные бек берёт из
 * signup-сессии; `session_id` в пути и есть capability (та же модель доверия, что у
 * `PATCH /signup-session`: валидность UUID + существование сессии, без проверки владельца).
 *
 * Любой сбой провайдера бек отдаёт как `200` c `status=fallback` — вызывающему остаётся
 * отличать «решение получено» от «ответа нет вовсе». Поэтому здесь: свой `timeout` (интейк
 * ждать не может, см. PRECHECK_TIMEOUT_MS) и `level: 'warning'` — недоступный прекчек это
 * штатный исход флоу (пациент идёт на фото), а не инцидент, и будить по нему никого не нужно.
 *
 * Ретраев нет намеренно (`retry: false` у клиента): пациент стоит на экране интейка.
 */
export async function kycPrecheck(
	sessionId: string,
	timeout: number,
	sentryContext?: SentryRequestContext,
): Promise<KycPrecheckResponse> {
	const response = await http.post<KycPrecheckResponse>(
		`/users/v1/signup-session/${encodeURIComponent(sessionId)}/kyc/precheck`,
		undefined,
		{ timeout, sentryContext: { flow: 'intake_form', step: 'kyc_precheck', level: 'warning', ...sentryContext } },
	)
	if (!response.data) throw new Error('kycPrecheck: empty response body')
	return response.data
}

// ── Account provisioning (unauthenticated, pre-auth) ─────────────────────────

// v3 signup (med-users PR #222, KOVA-474): pre-auth account creation for the
// no-Auth0-on-web funnel (DEV-35019, completed in DEV-35018 — this is now the ONLY
// way a web visitor gets an account). med-users mints the Auth0 identity itself
// from the session's email + phone (no password; phone becomes the MFA factor);
// the patient signs in by email OTP in the app. Fired fire-and-forget after the
// Telegra order exists (see src/modules/accountProvisioning.ts for call points
// and retry policy).
//
// 201 — created; 200 — an account already exists for this email (nothing
// written; the purchase is claimed at the user's next login instead); both carry
// `user_id` + `braze_id` since DEV-35777. 422 has two causes distinguished ONLY
// by `message` (asked med-users for a machine-readable error_code; still a gap):
// "no telegra order for this session yet; retry" is transient, "signup session
// missing or incomplete" (and the unreachable-for-us "invalid session id") is
// permanent. 503 — Auth0/auth-service unavailable or rate-limited → retry. Any
// other 4xx is outside the contract (schema drift, route not deployed, edge auth)
// and is terminal: retrying a deterministic answer only multiplies the noise.
type SignupV3Outcome = 'created' | 'already_exists' | 'retry_later' | 'session_broken' | 'rejected'

/**
 * Body of the 201/200 answers (`SignupV3Response` in openapi.yml). Both ids exist
 * on the backend the moment the account resolves, and med-users started returning
 * them in DEV-35777 so the funnel can put them back on `UserRegistrationData` —
 * they had no source on the web after Auth0 went away (DEV-35778).
 *
 * Everything is optional HERE on purpose, even though the spec marks both required:
 * until the backend change is released everywhere the funnel runs, 201/200 can still
 * come back with an empty body, and that must stay a successful provisioning — the
 * account is what matters, the ids only enrich an analytics event.
 */
interface SignupV3Response {
	user_id?: string
	/** Braze external_id; the backend sends null when med-crm is unavailable. */
	braze_id?: string | null
}

export interface SignupV3Result {
	outcome: SignupV3Outcome
	status: number
	/** Auth0 id (`auth0|…`) → analytics `main_user_id`. Success outcomes only. */
	userId?: string
	/** → analytics `braze_id`. Success outcomes only, and absent when med-crm was down. */
	brazeId?: string
}

export async function authSignupV3(sessionId: string): Promise<SignupV3Result> {
	const response = await http.post(
		'/users/v3/auth/signup',
		{ session_id: sessionId },
		{
			// Every status the contract names resolves here, so the capture interceptor
			// stays silent on expected flow states and the caller decides what to report.
			// Only 5xx (other than 503) and network failures reject — those are auto-captured
			// with this context and retried by the orchestrator.
			validateStatus: (s) => s < 500 || s === 503,
			sentryContext: { flow: 'account_provisioning', step: 'signup_v3' },
		},
	)
	const { status } = response
	if (status === 503 || status === 429) return { outcome: 'retry_later', status }
	if (status === 422) {
		const data: { message?: unknown } | string | null | undefined = response.data
		const message = typeof data === 'string' ? data : String(data?.message ?? '')
		// Unknown 422 wording → treat as transient; the orchestrator's attempt cap
		// bounds the damage, and a permanent misread would strand a paid user.
		const permanent = message.includes('missing or incomplete') || message.includes('invalid session id')
		return { outcome: permanent ? 'session_broken' : 'retry_later', status }
	}
	if (status >= 400) return { outcome: 'rejected', status }
	// An empty body stays a success (see SignupV3Response) — axios hands back '' for one,
	// so narrow before reading, exactly as the 422 branch does.
	const data: SignupV3Response | string | null | undefined = response.data
	const ids = data && typeof data === 'object' ? data : undefined
	return {
		outcome: status === 200 ? 'already_exists' : 'created',
		status,
		...(ids?.user_id && { userId: ids.user_id }),
		...(ids?.braze_id && { brazeId: ids.braze_id }),
	}
}

// ── Telegra intake (unauthenticated, session-scoped — med-users PR #169) ─────
// `/users/v1/signup-session/{session_id}/telegra/intake/*` — pre-auth proxy to
// clinic-proxy → Telegra for the DEV-34805 flow (the intake runs BEFORE the Auth0
// account exists; the session_id is the sole capability, same trust model as
// PATCH /signup-session). Shapes mirror the authed twins and follow
// docs/med-users-telegra-api.md (snake_case, flat locations).

const sessionIntakeBase = (sessionId: string) =>
	`/users/v1/signup-session/${encodeURIComponent(sessionId)}/telegra/intake`

type IntakeQuestionType = 'select' | 'date' | 'input' | 'upload' | 'agreement'
type IntakeQuestionMode = 'single' | 'multiple'

interface SelectOption {
	value: string
	label: string
	next?: string
}

interface AnswerHelper {
	answer_value: string
	helper_type: string
	text1?: string
}

export interface IntakeLocation {
	id: string
	location_type?: string
	label?: string
	description?: string
	type: IntakeQuestionType
	mode?: IntakeQuestionMode
	options?: SelectOption[]
	answer_helpers?: AnswerHelper[]
	previous?: string
	next?: string
}

interface QuestionnaireDefinition {
	id: string
	title?: string
	locations: IntakeLocation[]
}

interface ResponseAgreement {
	consent: boolean
	signature?: string
	consent_date?: string | null
}

export interface UploadedFile {
	name: string
	data: string
}

interface IntakeResponse {
	id: string
	location: string
	value?: string
	values?: string[]
	files?: UploadedFile[]
	agreement?: ResponseAgreement
}

export interface Instance {
	id: string
	status: 'in_progress' | 'completed'
	valid?: boolean
	current_location?: string
	questionnaire_id: string
	patient_id: string
	responses?: IntakeResponse[]
	expires_at?: string | null
	questionnaire: QuestionnaireDefinition
}

interface IntakeOrder {
	order_id: string
	order_number: string
	status: string
	expedited: boolean
	patient_id: string
	affiliate_id?: string
	project_id?: string
	questionnaire_instances: string[]
	product_variations: Array<{ id: string; quantity: number }>
}

interface AnswerAgreement {
	consent: boolean
	signature_base64?: string
}

/**
 * Одна запись ответа (`AnswerQuestionnaireLocationRequest` в openapi.yml): ровно одно из
 * value / values / files / agreement по type+mode локации. Используется и в PUT одного
 * ответа, и в батч-ручке `…/instance/{id}/answers` (см. modules/IntakeForm/answerGroups.ts).
 */
export interface AnswerBody {
	location: string
	value?: string
	values?: string[]
	files?: UploadedFile[]
	agreement?: AnswerAgreement
}

/**
 * Options for the read-only intake lookups below. The intake itself reports under
 * flow 'telegra_intake'; other readers (the /intake-done copy resolver) pass their
 * own `sentryContext` so their failures don't merge into the intake's GlitchTip
 * issues, and `allowMissing` turns a session-without-order answer (404/422) into an
 * empty list instead of a captured error — expected when the visitor never ordered.
 */
interface IntakeReadOptions {
	sentryContext?: SentryRequestContext
	allowMissing?: boolean
}

/** Ordered list of questionnaire instances the user must complete (from GetOrderBySession). */
export async function resolveInstances(
	sessionId: string,
	{ sentryContext, allowMissing = false }: IntakeReadOptions = {},
): Promise<{ quinstIds: string[] }> {
	const response = await http.get<IntakeOrder>(`${sessionIntakeBase(sessionId)}/order`, {
		sentryContext: sentryContext ?? { flow: 'telegra_intake', step: 'get_order' },
		...(allowMissing && { validateStatus: (s: number) => (s >= 200 && s < 300) || s === 404 || s === 422 }),
	})
	return { quinstIds: response.data?.questionnaire_instances || [] }
}

/**
 * Full instance state with embedded questionnaire definition. The `/schema`
 * endpoint duplicates `instance.questionnaire` — we don't call it separately.
 */
export async function getInstance(
	sessionId: string,
	instanceId: string,
	{ sentryContext }: Pick<IntakeReadOptions, 'sentryContext'> = {},
): Promise<Instance> {
	const response = await http.get<Instance>(
		`${sessionIntakeBase(sessionId)}/instance/${encodeURIComponent(instanceId)}`,
		{ sentryContext: sentryContext ?? { flow: 'telegra_intake', step: 'get_instance' } },
	)
	if (!response.data) throw new Error('telegra getInstance: empty response body')
	return response.data
}

/**
 * Батч ответов на ОДИН инстанс опросника (`AnswerQuestionnaireGroupRequest`).
 * `last_location` — навигационный якорь: от него Telegra пересчитывает `current_location`.
 */
export interface AnswerGroupBody {
	last_location: string
	responses: AnswerBody[]
}

/**
 * Атомарная запись всех ответов инстанса разом — то, ради чего интейк переехал в билдер:
 * юзер проходит опросник локально, а в конце мы отправляем по одному запросу на инстанс.
 *
 * В отличие от `answerLocation` курсор Telegra здесь не участвует: принимаются ответы на
 * локации, до которых пациент «не дошёл». Ветку бек НЕ валидирует — ответ на локацию вне
 * выбранной ветки сохранится молча и удалить его нечем, поэтому отправлять можно только
 * фактически пройденный путь (это гарантирует buildAnswerGroups).
 *
 * Ответ — перечитанный инстанс, так что `valid` отражает результат всего батча.
 * 413 (`photo_too_large`) — фото не влезло в лимит gRPC-сообщения clinic-proxy.
 */
export async function submitAnswerGroup(
	sessionId: string,
	instanceId: string,
	body: AnswerGroupBody,
): Promise<Instance> {
	const response = await http.put<Instance>(
		`${sessionIntakeBase(sessionId)}/instance/${encodeURIComponent(instanceId)}/answers`,
		body,
		{ sentryContext: { flow: 'telegra_intake', step: 'answers_batch' } },
	)
	if (!response.data) throw new Error('telegra submitAnswerGroup: empty response body')
	return response.data
}

export async function answerLocation(
	sessionId: string,
	instanceId: string,
	body: AnswerBody,
	shouldNavigateNext = true,
): Promise<Instance> {
	const response = await http.put<Instance>(
		`${sessionIntakeBase(sessionId)}/instance/${encodeURIComponent(instanceId)}/answer?should_navigate_next=${shouldNavigateNext}`,
		body,
		{
			sentryContext: {
				flow: 'telegra_intake',
				// Distinct step for photo answers so GlitchTip can filter upload failures
				// (DEV-34468) apart from ordinary question answers on the same endpoint.
				step: body.files?.length ? 'answer_upload' : 'answer',
			},
		},
	)
	if (!response.data) throw new Error('telegra answerLocation: empty response body')
	return response.data
}
